import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './InviteManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface PendingInvite {
  id: string;
  email: string;
  organizationId: string;
  organizationName: string;
  role: string;
  invitedBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
}

interface InviteManagementProps {
  onInviteSent?: () => void;
}

const InviteManagement: React.FC<InviteManagementProps> = ({ onInviteSent }) => {
  const { user, organization } = useAuth();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRole, setNewInviteRole] = useState<'admin' | 'employee'>('employee');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!organization?.id || !user?.email) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/organizations/${organization.id}/invites?include_inactive=${showInactive}&admin_email=${encodeURIComponent(user.email)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch invites');
      }
      
      const data = await response.json();
      setInvites(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch invites');
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, user?.email, showInactive]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization?.id || !user?.email || !newInviteEmail.trim()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/organizations/${organization.id}/invites?admin_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newInviteEmail.trim().toLowerCase(),
            role: newInviteRole,
          }),
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create invite');
      }
      
      // Reset form and refresh list
      setNewInviteEmail('');
      setNewInviteRole('employee');
      setShowForm(false);
      await fetchInvites();
      
      if (onInviteSent) {
        onInviteSent();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!organization?.id || !user?.email) return;
    
    if (!window.confirm('Are you sure you want to revoke this invite?')) {
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/organizations/${organization.id}/invites/${inviteId}?admin_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to revoke invite');
      }
      
      await fetchInvites();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invite');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'accepted':
        return 'status-accepted';
      case 'revoked':
        return 'status-revoked';
      case 'expired':
        return 'status-expired';
      default:
        return '';
    }
  };

  if (!organization) {
    return (
      <div className="invite-management">
        <div className="invite-empty">
          <p>No organization selected. Please select an organization first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-management">
      <div className="invite-header">
        <h3>Team Invites</h3>
        <div className="invite-header-actions">
          <label className="show-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button 
            className="invite-add-btn"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ Invite User'}
          </button>
        </div>
      </div>

      {error && (
        <div className="invite-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showForm && (
        <form className="invite-form" onSubmit={handleCreateInvite}>
          <div className="invite-form-row">
            <div className="invite-form-group">
              <label htmlFor="invite-email">Email Address</label>
              <input
                id="invite-email"
                type="email"
                value={newInviteEmail}
                onChange={(e) => setNewInviteEmail(e.target.value)}
                placeholder="user@example.com"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="invite-form-group">
              <label htmlFor="invite-role">Role</label>
              <select
                id="invite-role"
                value={newInviteRole}
                onChange={(e) => setNewInviteRole(e.target.value as 'admin' | 'employee')}
                disabled={isSubmitting}
              >
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button 
              type="submit" 
              className="invite-submit-btn"
              disabled={isSubmitting || !newInviteEmail.trim()}
            >
              {isSubmitting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>
      )}

      <div className="invite-list">
        {isLoading ? (
          <div className="invite-loading">Loading invites...</div>
        ) : invites.length === 0 ? (
          <div className="invite-empty">
            <p>No {showInactive ? '' : 'pending '}invites found.</p>
            <p className="invite-empty-hint">
              Click "Invite User" to invite team members to your organization.
            </p>
          </div>
        ) : (
          <table className="invite-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Invited</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className={invite.status !== 'pending' ? 'inactive-row' : ''}>
                  <td className="invite-email">{invite.email}</td>
                  <td>
                    <span className={`role-badge role-${invite.role}`}>
                      {invite.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(invite.status)}`}>
                      {invite.status}
                    </span>
                  </td>
                  <td className="invite-date">{formatDate(invite.createdAt)}</td>
                  <td className="invite-actions">
                    {invite.status === 'pending' && (
                      <button
                        className="revoke-btn"
                        onClick={() => handleRevokeInvite(invite.id)}
                        title="Revoke invite"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InviteManagement;
